import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import { MenuItem, TextField } from '@material-ui/core';
import * as Sentry from '@sentry/react';

import NavResponsiveModal, { ModalHeader } from '@/components/Navigation/NavResponsiveModal';
import { ModalContent, GreyBody, PrimaryButton, SpacerV1 } from '@/styles/blocks';
import { UserContext } from '@/components/App';

import PricingRow from './PricingRow';
import { SUPPLY_CHAIN_LEG_LABELS } from './constants';
import { GET_CORP_PRICING_OVERRIDES, INSERT_CORP_PRICING_OVERRIDE } from '../graphql';
import { CorpAdminContext, MARKETPLACE_KEY } from '../store';

const PricingModal = ({
    open,
    onClose,
    carrierPricing = [],
    shipperPricing = [],
    carrier_id,
    carrierLegs,
    shipper_id,
    shipperName,
}) => {
    const { admin_clients } = useContext(UserContext);
    const { callbacks } = useContext(CorpAdminContext);
    const { setNotification } = callbacks;

    const isMarketplace = carrier_id === MARKETPLACE_KEY;
    // For a real carrier, restrict legs to what the carrier supports. Marketplace and
    // legacy carriers without supply_chain_legs fall back to the full enum.
    // E2E supersedes FINAL_MILE — if a carrier has E2E, FINAL_MILE is dropped from options.
    const availableLegs = useMemo(() => {
        const base = carrierLegs?.length > 0 ? carrierLegs : (isMarketplace ? ['FINAL_MILE', 'MIDDLE_MILE'] : ['FINAL_MILE'])
        return base.includes('E2E') ? base.filter((l) => l !== 'FINAL_MILE') : base;
    }, [isMarketplace, carrierLegs]);

    const [side, setSide] = useState(isMarketplace ? 'shipper' : 'carrier');
    const [leg, setLeg] = useState(availableLegs[0] || '');

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const [insertOverride, { loading: inserting }] = useMutation(INSERT_CORP_PRICING_OVERRIDE, {
        update(cache, { data }) {
            const newOverride = data?.inserted;
            if (!newOverride) return;
            try {
                const existing = cache.readQuery({
                    query: GET_CORP_PRICING_OVERRIDES,
                    variables: { orgShipperIds },
                });
                if (!existing) return;
                cache.writeQuery({
                    query: GET_CORP_PRICING_OVERRIDES,
                    variables: { orgShipperIds },
                    data: { pricing_overrides: [...existing.pricing_overrides, newOverride] },
                });
            } catch {
                // cache miss is non-fatal
            }
        },
    });

    const handleAdd = async () => {
        const isCarrierSide = side === 'carrier';
        // Carrier-side: client_id=carrier, partner_client_id=shipper.
        // Shipper-side: client_id=shipper, partner_client_id=carrier (or null on marketplace).
        const object = {
            enabled: true,
            oms: false,
            client_id: isCarrierSide ? carrier_id : shipper_id,
            partner_client_id: isMarketplace && !isCarrierSide ? null : isCarrierSide ? shipper_id : carrier_id,
            marketplace_tier: isMarketplace ? 'T3_FULLY_MANAGED' : 'T1_INTEGRATIONS_ONLY',
            override_type: leg,
        };
        try {
            await insertOverride({ variables: { object } });
            setNotification?.({ severity: 'success', message: 'Pricing override added' });
        } catch (err) {
            console.error('Pricing override insert failed', err?.response?.data || err);
            Sentry.captureException(err);
            setNotification?.({ severity: 'error', message: 'Failed to add pricing override' });
        }
    };

    const hasCarrier = carrierPricing.length > 0;
    const hasShipper = shipperPricing.length > 0;
    const canAddCarrier = !isMarketplace && !!carrier_id;

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="md" fullWidth>
            <ModalHeader
                title={`Pricing${shipperName ? ` — ${shipperName}` : ''}`}
                onClose={onClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            <ModalContent
                css={css`
                    width: 100%;
                `}
            >
                <div
                    css={css`
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                        margin-bottom: 1rem;
                    `}
                >
                    <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Add"
                        value={side}
                        onChange={(e) => setSide(e.target.value)}
                        css={css`
                            min-width: 200px;
                        `}
                    >
                        {canAddCarrier && <MenuItem value="carrier">Carrier Pricing</MenuItem>}
                        <MenuItem value="shipper">Shipper Pricing</MenuItem>
                    </TextField>
                    <TextField
                        select
                        size="small"
                        variant="outlined"
                        label="Leg"
                        value={leg}
                        onChange={(e) => setLeg(e.target.value)}
                        disabled={availableLegs.length === 0}
                        css={css`
                            min-width: 160px;
                        `}
                    >
                        {availableLegs.map((value) => (
                            <MenuItem key={value} value={value}>
                                {SUPPLY_CHAIN_LEG_LABELS[value] || value}
                            </MenuItem>
                        ))}
                    </TextField>
                    <PrimaryButton onClick={handleAdd} disabled={inserting || !shipper_id || !leg}>
                        Add
                    </PrimaryButton>
                </div>

                {!hasCarrier && !hasShipper && <GreyBody>No pricing overrides configured for this shipper.</GreyBody>}

                {hasCarrier && (
                    <>
                        <GreyBody
                            css={css`
                                font-weight: 600;
                            `}
                        >
                            Carrier Pricing
                        </GreyBody>
                        <SpacerV1 />
                        {carrierPricing.map((pricing) => (
                            <PricingRow
                                key={pricing.pricing_override_id}
                                pricing={pricing}
                                shipper_id={shipper_id}
                                side="carrier"
                            />
                        ))}
                    </>
                )}

                {hasShipper && (
                    <>
                        <GreyBody
                            css={css`
                                font-weight: 600;
                                margin-top: ${hasCarrier ? '1rem' : '0'};
                            `}
                        >
                            Shipper Pricing
                        </GreyBody>
                        <SpacerV1 />
                        {shipperPricing.map((pricing) => (
                            <PricingRow
                                key={pricing.pricing_override_id}
                                pricing={pricing}
                                shipper_id={shipper_id}
                                side="shipper"
                            />
                        ))}
                    </>
                )}
            </ModalContent>
        </NavResponsiveModal>
    );
};

export default PricingModal;
